import { useEffect, useState } from 'react';
import { adoptTokens, api, currentUser, ensureSession } from '../api.ts';
import { CreateRoomModal } from './CreateRoomModal.tsx';
import { MatchmakingModal } from './MatchmakingModal.tsx';
import type { ClockConfig, FriendItem, HandicapConfig, LeaderboardEntry, Ruleset, SocialPlayer } from '@xiangqi/protocol';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface HomeProps {
  navigate(path: string): void;
}

export function Home({ navigate }: HomeProps) {
  const [joinCode, setJoinCode] = useState('');
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [matchmakingConfig, setMatchmakingConfig] = useState<{
    ruleset: Ruleset;
    clock?: ClockConfig | undefined;
    handicap?: HandicapConfig | undefined;
  } | null>(null);
  const [initialRuleset, setInitialRuleset] = useState<Ruleset>('standard');
  const [panel, setPanel] = useState<'friends' | 'ranking' | 'best'>('friends');
  const [friends, setFriends] = useState<FriendItem[]>([]);
  const [ranking, setRanking] = useState<LeaderboardEntry[]>([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SocialPlayer[]>([]);
  const [socialError, setSocialError] = useState<string | null>(null);
  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [newsIndex, setNewsIndex] = useState(0);
  const [showAuth, setShowAuth] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const user = currentUser();

  useEffect(() => {
    let cancelled = false;
    void ensureSession()
      .then(() => Promise.all([api.friends(), api.leaderboard()]))
      .then(([friendResult, leaderboardResult]) => {
        if (cancelled) return;
        setFriends(friendResult.friends);
        setRanking(leaderboardResult.leaderboard);
      })
      .catch(() => { if (!cancelled) setSocialError('Không tải được dữ liệu người chơi.'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (search.trim().length < 2) { setSearchResults([]); return undefined; }
    const timer = window.setTimeout(() => {
      void ensureSession().then(() => api.socialSearch(search.trim())).then((result) => {
        if (!cancelled) setSearchResults(result.players);
      }).catch(() => { if (!cancelled) setSearchResults([]); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [search]);

  const addFriend = async (player: SocialPlayer): Promise<void> => {
    setBusyUser(player.user_id);
    setSocialError(null);
    try {
      await api.sendFriendRequest(player.user_id);
      setSearchResults((items) => items.filter((item) => item.user_id !== player.user_id));
      setSocialError(`Đã gửi lời mời kết bạn đến ${player.display_name}.`);
    } catch (error) {
      setSocialError(error instanceof Error ? error.message : 'Gửi lời mời thất bại.');
    } finally { setBusyUser(null); }
  };

  const challenge = async (player: FriendItem): Promise<void> => {
    setBusyUser(player.user_id);
    try {
      const result = await api.challengeFriend(player.user_id, 'standard');
      navigate(`/r/${result.room.room_code}`);
    } catch (error) {
      setSocialError(error instanceof Error ? error.message : 'Không tạo được lời mời đấu.');
    } finally { setBusyUser(null); }
  };

  const handleQuickPlay = (ruleset: Ruleset = initialRuleset) => {
    setMatchmakingConfig({ ruleset });
  };

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  return (
    <main className="page home-v2">
      <header className="figma-home-header">
        <div className="figma-user-greeting">
          <span className="figma-avatar">帥</span>
          <div><span>Chào kỳ thủ,</span><strong>{user?.display_name ?? 'Khách'}</strong></div>
        </div>
        <button className="figma-icon-button" aria-label="Thông báo" onClick={() => navigate('/notifications')}>♧</button>
      </header>

      <section className="figma-news-section">
        <div className="figma-section-title"><h2>Tin tức</h2><button onClick={() => setNewsIndex((newsIndex + 1) % 3)}>Xem thêm →</button></div>
        <button className="figma-news-card" onClick={() => setNewsIndex((newsIndex + 1) % 3)}>
          <img src="/assets/board/board_bg.png" alt="Bàn cờ tướng" />
          <span className="news-shade" />
          <span className="news-copy"><strong>{['Khởi tranh hôm nay', 'Thử sức với Cờ Úp', 'Mời bạn bè vào bàn'][newsIndex]}</strong><small>{['Chơi một ván cờ mới ngay bây giờ', 'Khám phá những nước đi bất ngờ', 'Tạo phòng và gửi lời mời'][newsIndex]}</small></span>
          <span className="news-cta">Xem thêm</span>
        </button>
        <div className="figma-carousel-dots" aria-label={`Tin ${newsIndex + 1} trên 3`}>{[0, 1, 2].map((item) => <button key={item} className={newsIndex === item ? 'active' : ''} aria-label={`Tin ${item + 1}`} onClick={() => setNewsIndex(item)} />)}</div>
      </section>

      <section className="figma-games-section">
        <div className="figma-section-title"><h2>Chế độ chơi</h2><button onClick={() => { setInitialRuleset('standard'); setShowRoomModal(true); }}>Tùy chọn →</button></div>
        <div className="figma-game-grid">
          {([
            ['standard', 'Cờ Tướng', 'red_chariot.png', 'Luật chuẩn truyền thống'],
            ['blind', 'Cờ Úp', 'black_cannon.png', 'Cờ úp kinh điển'],
            ['blind_si_chua', 'Sĩ Chúa', 'red_royal_advisor.png', 'Biến thể Sĩ Chúa'],
            ['blind_tuong_vuong', 'Tượng Vương', 'red_royal_elephant.png', 'Biến thể Tượng Vương'],
          ] as const).map(([ruleset, title, asset, description]) => (
            <button className="figma-game-tile" key={ruleset} onClick={() => { setInitialRuleset(ruleset); setShowRoomModal(true); }}>
              <span className="game-piece-art"><img src={`/assets/pieces/${asset}`} alt="" /></span>
              <strong>{title}</strong><small>{description}</small>
            </button>
          ))}
        </div>
        <div className="figma-quick-actions">
          <button className="figma-quick-play" onClick={() => handleQuickPlay('standard')}>⚔ Chơi ngay</button>
          <button onClick={() => { setInitialRuleset('standard'); setShowRoomModal(true); }}>＋ Tạo phòng bạn bè</button>
          <form onSubmit={(event) => { event.preventDefault(); if (joinCode.trim().length >= 4) navigate(`/r/${joinCode.trim().toUpperCase()}`); }}>
            <input value={joinCode} onChange={(event) => setJoinCode(event.target.value.toUpperCase())} maxLength={10} placeholder="Mã phòng..." aria-label="Mã phòng" />
            <button type="submit" disabled={joinCode.trim().length < 4}>Vào phòng</button>
          </form>
        </div>
      </section>

      <section className="figma-information-section">
        <div className="figma-section-title"><h2>Thông tin</h2><button onClick={() => navigate(panel === 'friends' ? '/friends' : '/leaderboard')}>Mở rộng →</button></div>
        <div className="figma-information-card">
          <div className="figma-information-tabs">
            <button className={panel === 'friends' ? 'active' : ''} onClick={() => setPanel('friends')}>Bạn bè</button>
            <button className={panel === 'ranking' ? 'active' : ''} onClick={() => setPanel('ranking')}>Xếp hạng</button>
            <button className={panel === 'best' ? 'active' : ''} onClick={() => setPanel('best')}>Gợi ý đấu</button>
          </div>
          {panel === 'friends' && <div className="figma-social-search"><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Tìm theo tên hoặc username" aria-label="Tìm người chơi" />{searchResults.map((player) => <div className="figma-player-row" key={player.user_id}><PlayerAvatar name={player.display_name} /><div className="figma-player-details"><strong>{player.display_name}</strong><small>{player.username ? `@${player.username}` : 'Khách'}</small></div><button onClick={() => void addFriend(player)} disabled={busyUser === player.user_id}>{busyUser === player.user_id ? '…' : '＋'}</button></div>)}</div>}
          {socialError && <p className="figma-inline-message" role="status">{socialError}</p>}
          <div className="figma-player-list">
            {panel === 'friends' && (friends.length ? friends.slice(0, 4).map((player) => <div className="figma-player-row" key={player.user_id}><PlayerAvatar name={player.display_name} /><div className="figma-player-details"><strong>{player.display_name}</strong><small>{player.wins} thắng · {player.total_games} ván</small></div><button aria-label={`Nhắn tin ${player.display_name}`} onClick={() => navigate(`/chat?with=${player.user_id}`)}>•••</button></div>) : <div className="figma-empty-row"><strong>Chưa có bạn bè</strong><small>Tìm người chơi ở ô phía trên để kết bạn.</small></div>)}
            {panel === 'ranking' && (ranking.length ? ranking.slice(0, 4).map((player) => <div className="figma-player-row" key={player.user_id}><span className="figma-rank-number">{player.rank}</span><PlayerAvatar name={player.display_name} /><div className="figma-player-details"><strong>{player.display_name}</strong><small>{player.wins} thắng · {player.win_rate}% thắng</small></div><button aria-label={`Xem hồ sơ ${player.display_name}`} onClick={() => navigate('/leaderboard')}>◉</button></div>) : <div className="figma-empty-row">Chưa có bảng xếp hạng</div>)}
            {panel === 'best' && (friends.length ? friends.slice(0, 4).map((player) => <div className="figma-player-row" key={player.user_id}><PlayerAvatar name={player.display_name} /><div className="figma-player-details"><strong>{player.display_name}</strong><small>Đối thủ gợi ý · {player.wins} thắng</small></div><button className="figma-challenge-button" disabled={busyUser === player.user_id} onClick={() => void challenge(player)}>{busyUser === player.user_id ? '…' : 'Đấu'}</button></div>) : <div className="figma-empty-row"><strong>Chưa có đối thủ gợi ý</strong><small>Kết bạn để gửi lời mời đấu.</small></div>)}
          </div>
          <button className="figma-show-more" onClick={() => navigate(panel === 'friends' ? '/friends' : '/leaderboard')}>Xem tất cả →</button>
        </div>
      </section>

      {deferredPrompt && (
        <div className="pwa-install-card">
          <div className="pwa-install-icon">📲</div>
          <div className="pwa-install-details">
            <span className="pwa-install-title">Cài đặt Kỳ Chiến</span>
            <span className="pwa-install-desc">
              Thêm Kỳ Chiến vào màn hình chính để chơi toàn màn hình, mượt mà hơn.
            </span>
          </div>
          <button
            type="button"
            className="pwa-install-button"
            onClick={() => void handleInstallApp()}
          >
            Cài đặt
          </button>
        </div>
      )}

      {showRoomModal && (
        <CreateRoomModal
          initialRuleset={initialRuleset}
          onClose={() => setShowRoomModal(false)}
          navigate={navigate}
          onStartMatchmaking={(ruleset, clock, handicap) => {
            setShowRoomModal(false);
            setMatchmakingConfig({
              ruleset,
              clock,
              ...(handicap ? { handicap } : {}),
            });
          }}
        />
      )}

      {matchmakingConfig && (
        <MatchmakingModal
          ruleset={matchmakingConfig.ruleset}
          clock={matchmakingConfig.clock}
          handicap={matchmakingConfig.handicap}
          onClose={() => setMatchmakingConfig(null)}
          navigate={navigate}
        />
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </main>
  );
}

function PlayerAvatar({ name }: { name: string }) {
  return <span className="figma-player-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>;
}

function AuthModal({ onClose }: { onClose(): void }) {
  const [mode, setMode] = useState<'login' | 'register'>('register');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await ensureSession();
      const tokens =
        mode === 'register' ? await api.register(username, password) : await api.login(username, password);
      adoptTokens(tokens);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Thao tác thất bại');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true">
        <h2>{mode === 'register' ? 'Đăng ký tài khoản' : 'Đăng nhập'}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <input
            placeholder="Tên đăng nhập"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={busy}
            autoFocus
          />
          <input
            type="password"
            placeholder="Mật khẩu"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
          {error && <p className="error">{error}</p>}
          <button className="primary" type="submit" disabled={busy || !username || !password}>
            {busy ? 'Đang xử lý…' : mode === 'register' ? 'Đăng ký' : 'Đăng nhập'}
          </button>
        </form>
        <button
          className="link close"
          onClick={() => setMode(mode === 'register' ? 'login' : 'register')}
        >
          {mode === 'register' ? 'Đã có tài khoản? Đăng nhập' : 'Chưa có tài khoản? Đăng ký'}
        </button>
        <button className="link close" onClick={onClose}>
          Đóng
        </button>
      </div>
    </div>
  );
}
